import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'ia_original_texts';
const MAX_ENTRIES = 200;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface OriginalTextEntry {
    text: string;
    /** epoch ms */
    savedAt: number;
}

/** documentId -> fieldId -> backup */
type OriginalTextStore = Record<string, Record<string, OriginalTextEntry>>;

/**
 * Guarda o texto que o campo tinha ANTES de uma ação de IA sobrescrever
 * (correção ortográfica, descrição automática, usar como prompt, corrigir todos
 * os textos). Alimenta o "Desfazer" do toast pós-IA e a opção "Voltar ao texto
 * original" no menu de IA do card.
 *
 * Persistido no localStorage para sobreviver a reload/aba fechada. Política: o
 * PRIMEIRO backup de um campo vence (é o texto do usuário; os seguintes já
 * seriam texto de IA). Uma edição manual do campo limpa o backup, porque o novo
 * texto digitado passa a ser o "original".
 */
@Injectable({ providedIn: 'root' })
export class OriginalTextService {
    private _cache: OriginalTextStore | null = null;

    /**
     * Muda a cada escrita. O app é zoneless: sem um signal, um template que
     * chama [has] não reavaliaria quando o backup é criado/descartado e a tag
     * "Voltar ao original" só apareceria no próximo ciclo por outro motivo.
     */
    private _version = signal(0);

    /** O campo tem texto pré-IA guardado? (reativo: ver [_version]) */
    public has(documentId: string, fieldId: string): boolean {
        this._version();
        if (!documentId || !fieldId) return false;
        return this._load()[documentId]?.[fieldId] !== undefined;
    }

    public get(documentId: string, fieldId: string): string | null {
        if (!documentId || !fieldId) return null;
        return this._load()[documentId]?.[fieldId]?.text ?? null;
    }

    /**
     * Guarda o texto pré-IA do campo. Backup existente é mantido (primeiro
     * vence); texto vazio não gera backup.
     */
    public save(documentId: string, fieldId: string, text: string): void {
        this.saveMany(documentId, { [fieldId]: text });
    }

    /**
     * Versão em lote (corrigir todos os textos): uma escrita só no storage.
     * Retorna os fieldIds cujo backup foi CRIADO agora (os que já existiam
     * ficam de fora), permitindo desfazer o snapshot se a IA falhar.
     */
    public saveMany(documentId: string, texts: Record<string, string>): string[] {
        if (!documentId) return [];

        const store = this._load();
        const fields = (store[documentId] ??= {});
        const now = Date.now();
        const created: string[] = [];

        for (const [fieldId, text] of Object.entries(texts)) {
            if (!fieldId || !text || fields[fieldId] !== undefined) continue;
            fields[fieldId] = { text, savedAt: now };
            created.push(fieldId);
        }

        if (!created.length) return [];

        this._prune(store);
        this._persist(store);
        return created;
    }

    /**
     * Descarta o backup do campo (após restaurar, ou quando o usuário edita o
     * campo manualmente).
     */
    public clear(documentId: string, fieldId: string): void {
        this.clearMany(documentId, [fieldId]);
    }

    /** Versão em lote (rollback do snapshot quando a correção em massa falha). */
    public clearMany(documentId: string, fieldIds: string[]): void {
        if (!documentId || !fieldIds?.length) return;

        const store = this._load();
        const fields = store[documentId];
        if (!fields) return;

        let changed = false;
        for (const fieldId of fieldIds) {
            if (fields[fieldId] === undefined) continue;
            delete fields[fieldId];
            changed = true;
        }
        if (!changed) return;

        if (!Object.keys(fields).length) delete store[documentId];
        this._persist(store);
    }

    private _load(): OriginalTextStore {
        if (this._cache) return this._cache;

        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const parsed = raw ? (JSON.parse(raw) as OriginalTextStore) : {};
            this._cache = this._sanitize(parsed);
            this._prune(this._cache);
        } catch {
            // Storage corrompido: recomeça sem backups em vez de propagar.
            this._cache = {};
        }

        return this._cache;
    }

    /** Descarta qualquer coisa que não tenha o shape esperado. */
    private _sanitize(parsed: unknown): OriginalTextStore {
        const store: OriginalTextStore = {};
        if (!parsed || typeof parsed !== 'object') return store;

        for (const [documentId, fields] of Object.entries(parsed as Record<string, unknown>)) {
            if (!fields || typeof fields !== 'object') continue;

            const valid: Record<string, OriginalTextEntry> = {};
            for (const [fieldId, entry] of Object.entries(fields as Record<string, unknown>)) {
                const candidate = entry as Partial<OriginalTextEntry>;
                if (typeof candidate?.text === 'string' && typeof candidate?.savedAt === 'number') {
                    valid[fieldId] = { text: candidate.text, savedAt: candidate.savedAt };
                }
            }
            if (Object.keys(valid).length) store[documentId] = valid;
        }

        return store;
    }

    /**
     * TTL + teto global de entradas: o localStorage guarda uma string única,
     * então o mapa não pode crescer sem limite.
     */
    private _prune(store: OriginalTextStore): void {
        const cutoff = Date.now() - TTL_MS;
        const all: { documentId: string; fieldId: string; savedAt: number }[] = [];

        for (const [documentId, fields] of Object.entries(store)) {
            for (const [fieldId, entry] of Object.entries(fields)) {
                if (entry.savedAt < cutoff) {
                    delete fields[fieldId];
                    continue;
                }
                all.push({ documentId, fieldId, savedAt: entry.savedAt });
            }
            if (!Object.keys(fields).length) delete store[documentId];
        }

        let excess = all.length - MAX_ENTRIES;
        if (excess <= 0) return;

        // Os mais antigos caem primeiro.
        all.sort((first, second) => first.savedAt - second.savedAt);
        for (const entry of all) {
            if (excess <= 0) break;
            delete store[entry.documentId]?.[entry.fieldId];
            excess--;
        }

        for (const [documentId, fields] of Object.entries(store)) {
            if (!Object.keys(fields).length) delete store[documentId];
        }
    }

    private _persist(store: OriginalTextStore): void {
        this._cache = store;
        this._version.update(version => version + 1);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        } catch {
            // Sem espaço / storage indisponível: o backup vale só para a sessão.
        }
    }
}
